import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Controller, SubmitHandler } from 'react-hook-form'
import Swal from 'sweetalert2'
import { FormInput } from '../../components/forms/input/FormInput'
import { FormSelect } from '../../components/forms/select/FormSelect'
import { AppCodigosCatalogo } from '../../utils/Constantes'
import { objetoToArreglo, arregloToObjeto } from '../../utils/ObjetosUtil'
import {
  useRecuperarOpcCatalogoQuery,
  useRecuperarSitioPorIdQuery,
  useGuardarSitioMutation,
  useActualizarSitioMutation,
  useActualizarNivelContactoMutation,
  useLazyRecuperarUsuarioAutocompleteQuery,
  useEliminarContactoPorIdMutation,
  useGuardarContactoMutation,
} from '../../redux/api'
import type {} from 'redux-thunk/extend-redux'
import { ContactoSitio, ProcesoActividad, NuevoObjResponsable, Nivel } from './models'
import { CargaInfo } from '../../components/layout/CargaInfo'
import { style, TSitioForm, useSitioForm } from './models/sitioForm.type'
import { Contactos } from './components'
import { Opcion } from '../../models/catalogo.type'
import { Cargando } from '../../components/layout/Cargando'
import { Usuario } from '../usuarios/models'

const AgregarEditarSitio = () => {
  const navigate = useNavigate()
  const [recuperarUsuarioAutocomplete] = useLazyRecuperarUsuarioAutocompleteQuery()
  const { idSitio } = useParams()
  const [sitioCargado, setSitioCargado] = useState(false)
  const [actualizarNivel] = useActualizarNivelContactoMutation()
  const [arrResponsables, setResponsables] = useState<ContactoSitio[]>([])
  const [arrResponsablesObj, setResponsablesObj] = useState<NuevoObjResponsable>({})
  const [eliminarContacto] = useEliminarContactoPorIdMutation()
  const [guardarContacto] = useGuardarContactoMutation()
  const [divisionNegocio, setDivisionNegocio] = useState(undefined)
  // UI: `nivelGlobal` agrupa responsables por nivel para drag & drop.
  // Regla de negocio en esta pantalla: no se permiten niveles intermedios vacios.
  const [nivelGlobal, setNivelGlobal] = useState<Nivel>({
    '1': {
      titulo: 'Nivel 1',
      responsables: [],
    },
    '2': {
      titulo: 'Nivel 2',
      responsables: [],
    },
    '3': {
      titulo: 'Nivel 3',
      responsables: [],
    },
  })
  const isModoEditar = !!idSitio

  const formSitio = useSitioForm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    resetField,
    getValues,
  } = formSitio

  // Recupera el sitio por id solo en modo editar.
  const {
    data: sitio,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useRecuperarSitioPorIdQuery(idSitio!, {
    skip: !isModoEditar,
  })

  //Hook guardar sitio
  const [
    guardarSitio,
    {
      data,
      isLoading: isLoadingGuardar,
      error: errorGuardar,
      isSuccess: isSuccessGuardar,
      isError: isErrorGuardar,
      reset: resetGuardar,
    },
  ] = useGuardarSitioMutation()

  //Hook actualizar sitio
  const [
    actualizarSitio,
    {
      isLoading: isLoadingActualizar,
      error: errorAcualizar,
      isSuccess: isSuccessActualizar,
      isError: isErrorActualizar,
    },
  ] = useActualizarSitioMutation()

  //Hooks catalogos
  const { data: catRazonesSociales } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.RAZON_SOCIAL)
  const { data: catDivisionesNegocio } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.DIVISION_NEGOCIO
  )
  const { data: catProcesosActividades } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.PROCESO_ACTIVIDAD
  )
  const { data: catEstados } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.ESTADOS_REPUBLICA)

  /**
   * @description Deshabilita funcionalidades en funcion de el modo edicion y la division de negocio
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 28/02/2023
   *
   * @returns true or false
   */
  const isBusquedaDeshabilitada = () => {
    return !isModoEditar && !divisionNegocio
  }

  /**
   * @description Función que determina el nivel al que se mueve la card de responsable
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 03/03/2023
   *
   */
  const handleDragEnd = (arg: any) => {
    // `react-beautiful-dnd` provee source/destination; el estado se actualiza de forma optimista.
    // En modo editar, adicionalmente se persiste el cambio de nivel via API.
    const sourceDroppableId = Number(arg.source.droppableId)
    const destinationDroppableId = Number(arg.destination?.droppableId)

    const fuenteVacia = nivelGlobal[sourceDroppableId].responsables.length - 1 === 0
    const destinoVacio = nivelGlobal[destinationDroppableId - 1]?.responsables.length === 0

    const movimientoInvalido =
      (sourceDroppableId < destinationDroppableId && fuenteVacia) ||
      (sourceDroppableId > destinationDroppableId &&
        fuenteVacia &&
        (nivelGlobal[sourceDroppableId + 1]?.responsables?.length ?? 0) !== 0) ||
      (sourceDroppableId < destinationDroppableId && destinoVacio)

    if (movimientoInvalido) {
      toast.error('No es posible dejar el nivel sin responsable', { style })
      return
    }

    if (
      !arg.destination ||
      (arg.destination.index === arg.source.index &&
        arg.destination.droppableId === arg.source.droppableId)
    ) {
      return
    }

    const copiaResponsables = {
      ...nivelGlobal[arg.source.droppableId].responsables[arg.source.index],
    }
    const nivelActualizado = arg.destination.droppableId
    let arregloContacto = null

    setNivelGlobal((prev) => {
      const actualizarNivel = { ...prev }
      actualizarNivel[arg.source.droppableId].responsables.splice(arg.source.index, 1)
      actualizarNivel[arg.destination.droppableId].responsables.splice(
        arg.destination.index,
        0,
        copiaResponsables
      )
      return actualizarNivel
    })

    const index = isModoEditar
      ? arrResponsables.findIndex(
          (contacto) => contacto.idContacto === copiaResponsables.idContacto
        )
      : arrResponsables.findIndex((contacto) => contacto.idUsuario === copiaResponsables.idUsuario)

    if (index >= 0) {
      const arregloNuevo = [...arrResponsables]
      arregloNuevo[index] = {
        ...arregloNuevo[index],
        nivel: nivelActualizado,
      }
      const arregloObjNuevo = arregloToObjeto(arregloNuevo)
      setResponsablesObj(arregloObjNuevo)
      setResponsables(arregloNuevo)

      arregloContacto = isModoEditar
        ? {
            idContacto: copiaResponsables.idContacto,
            nivel: nivelActualizado,
          }
        : null
    }

    if (arregloContacto) {
      actualizarNivel(arregloContacto)
        .unwrap()
        .then(() => {
          toast.success('¡Nivel de responsable actualizado!', { style })
        })
        .catch((error) => {
          toast.error('¡Error al actualizar el nivel del responsable!', { style })
          console.error(error)
        })
    }
  }

  /**
   * @description Agrega opciones al select de autocompletado
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 28/02/2023
   *
   * @param nombreResponsable
   * @param callback Regresa el arreglo de posibles opciones
   */
  const agregaOpcionesSelect = (nombreResponsable: string, callback: Function) => {
    // Autocomplete dependiente: en modo crear no se permite buscar hasta seleccionar Division de negocio.
    if (nombreResponsable && nombreResponsable.length >= 1) {
      recuperarUsuarioAutocomplete({ nombre: nombreResponsable, divisionNegocio, idSitio })
        .unwrap()
        .then((resp) => {
          callback(
            resp?.map((usuario: Usuario) => ({
              etiqueta: `${usuario.nombre} ${usuario.apellidoPaterno} ${usuario.apellidoMaterno}`,
              nombre: usuario.nombre,
              idUsuario: usuario.idUsuario,
              nivel: usuario.nivel ? usuario.nivel : '1',
              apellidoPaterno: usuario.apellidoPaterno,
              estatus: usuario.estatus,
              ausente: usuario.ausente,
              correo: usuario.correo,
              perfil: usuario.perfil,
              apellidoMaterno: usuario.apellidoMaterno,
              telefono: usuario.telefono,
              descPosicion: usuario.descPosicion,
              divisionDeNegocio: usuario.divisionesNegocio,
              idContacto: usuario.idContacto,
            }))
          )
        })
        .catch(console.error)
    }
  }
  const posicionarNuevoContacto = (
    nuevoObjResponsable: NuevoObjResponsable,
    numResponsables: number
  ) => {
    // UX: nuevos responsables se agregan por default al Nivel 1.
    setNivelGlobal((prev) => ({
      ...prev,
      '1': {
        ...prev['1'],
        responsables: [...prev['1'].responsables, nuevoObjResponsable[numResponsables + 1]],
      },
    }))
  }
  /**
   * @description Función para agregar un responsable
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 08/03/2023
   *
   */
  const agregarResponsableCard = () => {
    // Flujo:
    // - Crear: solo actualiza estado local; los contactos se mandan junto con el sitio al guardar.
    // - Editar: persiste el contacto inmediatamente via API (asociar responsable) y actualiza estado.
    const responsableNuevo = getValues('nombreResponsable')
    if (responsableNuevo !== undefined) {
      const numResponsables = Object.keys(arrResponsablesObj).length
      const nuevoResponsable = {
        nombre: responsableNuevo.nombre,
        idUsuario: responsableNuevo.idUsuario,
        apellidoPaterno: responsableNuevo.apellidoPaterno,
        apellidoMaterno: responsableNuevo.apellidoMaterno,
        posicion: responsableNuevo.descPosicion,
        nivel: responsableNuevo.nivel,
        inicialesResponsable:
          responsableNuevo.nombre.charAt(0) + responsableNuevo.apellidoPaterno.charAt(0),
        perfil: responsableNuevo.perfil,
        correo: responsableNuevo.correo,
        estatus: responsableNuevo.estatus,
        ausente: responsableNuevo.ausente,
        telefono: responsableNuevo.telefono,
        descPosicion: responsableNuevo.descPosicion,
        divisionDeNegocio: responsableNuevo.divisionesNegocio,
        idContacto: responsableNuevo.idContacto,
      }
      const idUsuario = nuevoResponsable.idUsuario
      const nivel = nuevoResponsable.nivel
      const nuevoObjResponsable: NuevoObjResponsable = {
        ...arrResponsablesObj,
        [numResponsables + 1]: nuevoResponsable,
      }
      const arregloMutado = objetoToArreglo(nuevoObjResponsable)
      if (!isModoEditar) {
        setResponsables(arregloMutado)
        setResponsablesObj(nuevoObjResponsable)
        posicionarNuevoContacto(nuevoObjResponsable, numResponsables)
        resetField('nombreResponsable')
      } else {
        const toastId = toast.loading('Asociando responsable', { style })
        guardarContacto({ idSitio, idUsuario, nivel })
          .unwrap()
          .then((resp) => {
            nuevoResponsable.idContacto = resp.idContacto
            const objetoResponsable: NuevoObjResponsable = {
              ...arrResponsablesObj,
              [numResponsables + 1]: nuevoResponsable,
            }
            const nuevoArregloMutado = objetoToArreglo(objetoResponsable)
            setResponsables(nuevoArregloMutado)
            setResponsablesObj(nuevoObjResponsable)
            posicionarNuevoContacto(nuevoObjResponsable, numResponsables)
            toast.success('¡Responsable asociado!', {
              id: toastId,
              style,
            })
            resetField('nombreResponsable')
          })
          .catch((error) => {
            toast.error('¡Error al asociar el responsable', {
              id: toastId,
              style,
            })
            console.error(error)
          })
      }
    } else {
      toast.error('Seleccione un responsable', { style })
    }
  }
  const mensajeError = () => {
    // Normaliza el mensaje de error proveniente de RTK Query/SW.
    const err: any = isModoEditar ? errorAcualizar : errorGuardar
    const mensaje = err.data ? err.data.moreInfo : err.error ? err.error : ''
    Swal.fire({
      title: 'Algo salió mal',
      icon: 'error',
      html: `Por favor valida la información o intentalo mas tarde <br/><p class='mt-5 fs-7'>(${err.status}) ${mensaje}</p>`,
      showCancelButton: true,
      confirmButtonText: 'Intentar de nuevo',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmit(onSubmit)()
      }
    })
  }
  const mensajeGuardado = () => {
    Swal.fire({
      title: '¡Sitio guardado!',
      icon: 'success',
      text: 'El sitio se encuentra registrado.',
      showCancelButton: true,
      confirmButtonText: 'Seguir editando',
      cancelButtonText: 'Regresar a sitios',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        resetGuardar()
        navigate(`/sitios/editar/${data.idSitio}`)
      } else {
        navigate('/sitios')
      }
    })
  }
  const verificarCondicionParaEliminar = (nivelGlobal: Nivel, nivelKey: any) =>
    // Regla: no se permite dejar al sitio sin responsables, ni dejar niveles intermedios sin responsables.
    nivelGlobal[nivelKey].responsables.length === 1 &&
    ((nivelGlobal[+nivelKey + 1]?.responsables?.length ?? 0) !== 1 ||
      (nivelGlobal[nivelKey - 1]?.responsables?.length ?? 0) !== 1) &&
    nivelKey !== Object.keys(nivelGlobal).length

  /**
   * @description Función que elimina un usuario asignado como responsable
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 08/03/2023
   *
   * @param idResponsable id del usuario recuperado
   * @param nivelKey nivel en el que se encuentra el usuario
   */
  const eliminarResponsable = (id: string, nivelKey: any) => {
    if (verificarCondicionParaEliminar(nivelGlobal, nivelKey)) {
      toast.error('No es posible dejar el sitio sin ningún responsable', { style })
      return
    }

    if (!isModoEditar) {
      // En modo crear solo se toca estado local.
      setNivelGlobal((prev) => {
        const nuevoNivel = { ...prev[nivelKey] }
        const nuevosResponsables = nuevoNivel.responsables.map(
          (responsable: { idUsuario: string | number }) => {
            const responsableObj = arrResponsablesObj[responsable.idUsuario]
            return responsableObj ? { ...responsable, ...responsableObj } : responsable
          }
        )
        nuevoNivel.responsables = nuevosResponsables.filter(
          (responsable: { idUsuario: string }) => responsable.idUsuario !== id
        )

        const nuevoArrResponsableObj = { ...arrResponsablesObj }
        delete nuevoArrResponsableObj[id]

        const arregloMutado = objetoToArreglo(nuevoArrResponsableObj)
        setResponsablesObj(nuevoArrResponsableObj)
        setResponsables(arregloMutado)

        return { ...prev, [nivelKey]: nuevoNivel }
      })
    } else {
      // En modo editar se elimina via API y luego se refleja en UI.
      if (arrResponsables.length <= 1) {
        toast.error('No es posible dejar el sitio sin ningún responsable', { style })
        return
      }

      const toastId = toast.loading('Eliminando responsable', { style })
      eliminarContacto(id)
        .then(() => {
          setNivelGlobal((prev) => {
            const nuevoNivel = { ...prev[nivelKey] }
            nuevoNivel.responsables = nuevoNivel.responsables.filter(
              (responsable: { idContacto: string }) => responsable.idContacto !== id
            )

            const nuevoArrResponsableObj = { ...arrResponsablesObj }
            delete nuevoArrResponsableObj[id]

            const arregloMutado = objetoToArreglo(nuevoArrResponsableObj)
            setResponsablesObj(nuevoArrResponsableObj)
            setResponsables(arregloMutado)

            return { ...prev, [nivelKey]: nuevoNivel }
          })

          toast.success('¡Responsable eliminado!', {
            id: toastId,
            style,
          })
        })
        .catch((error: any) => {
          toast.error('¡Error al eliminar el responsable!', {
            id: toastId,
            style,
          })
          console.error(error)
        })
    }
  }

  /**
   * @description Función que agrega un nivel al area de responsables
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 03/03/2023
   *
   */
  const agregarNivel = () => {
    // Se puede agregar N niveles; se permite eliminar solo el ultimo si esta vacio (ver NivelesCards).
    const numeroObjetosActuales = Object.keys(nivelGlobal).length
    const numeroNivel = numeroObjetosActuales + 1
    const nuevoNivel = {
      titulo: `Nivel ${numeroNivel}`,
      responsables: [],
    }
    const nuevoAgregado = { ...nivelGlobal }
    nuevoAgregado[`${numeroNivel}`] = nuevoNivel
    setNivelGlobal(nuevoAgregado)
  }

  /**
   * @description Función que elimina un nivel
   *
   * @author sergio.guerrero, Hábil MX
   * @version 1.0.0, 08/03/2023
   *
   * @param key Nombre del nivel
   */
  const eliminarNivel = (key: string) => {
    const nuevoArreglo = { ...nivelGlobal }
    delete nuevoArreglo[key]
    setNivelGlobal(nuevoArreglo)
  }
  useEffect(() => {
    if (isSuccessActualizar) {
      toast.success('¡Sitio actualizado!', { style })
    } else if (!sitioCargado && isModoEditar && isSuccess) {
      // Hidratacion en modo editar: mapea modelo API a shape del formulario (Opcion) y
      // arma estructuras auxiliares para el gestor de responsables por nivel.
      reset({
        ...sitio,
        razonSocial: {
          valor: sitio.razonSocial,
          etiqueta: sitio.descRazonSocial,
          ext5: sitio.rfc,
        },
        divisionDeNegocio: {
          valor: sitio.divisionDeNegocio,
          etiqueta: sitio.descDivisionDeNegocio,
        },
        procesosActividades: sitio.procesosActividades?.map((pa: ProcesoActividad) => {
          return {
            valor: pa.nombre,
            etiqueta: pa.descripcion,
          }
        }),
        estado: {
          valor: sitio.estado,
          etiqueta: sitio.descEstado,
        },
      })
      const responsables: any = sitio.contactos?.map((r) => {
        return {
          ...r,
          inicialesResponsable: r.nombre.charAt(0) + r.apellidoPaterno.charAt(0),
          perfil: r.perfil,
          nivel: r.nivel,
        }
      })
      const responsableObj = arregloToObjeto(responsables)
      setResponsablesObj(responsableObj)
      setResponsables(responsables)
      const responsablesAgrupados =
        responsables &&
        responsables.reduce((responsablesNuevo: any, responsable: any) => {
          const nivel = responsable.nivel ? responsable.nivel.toString() : ''
          if (!responsablesNuevo[nivel]) {
            responsablesNuevo[nivel] = {
              titulo: `Nivel ${nivel}`,
              responsables: [],
            }
          }
          responsablesNuevo[nivel].responsables.push(responsable)
          return responsablesNuevo
        }, {})
      setNivelGlobal(responsablesAgrupados)
      setSitioCargado(true)
    }
    if (isSuccessGuardar) {
      mensajeGuardado()
    }
    if (isErrorGuardar || isErrorActualizar) {
      mensajeError()
    }
  }, [
    isModoEditar,
    isSuccess,
    isSuccessGuardar,
    isError,
    isErrorActualizar,
    isSuccessActualizar,
    isErrorGuardar,
  ])

  /**
   * @description Función que agregará la información del nuevo sitio
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 11/11/2022
   * @param data : datos del formulario
   */
  const onSubmit: SubmitHandler<TSitioForm> = (data: TSitioForm) => {
    // Regla: en modo crear debe existir al menos 1 responsable antes de guardar.
    if (!isModoEditar && arrResponsables.length === 0) {
      toast.error('Agrega al menos un responsable de sitio', { style })
      return
    }
    // Contrato API: el endpoint de Sitios espera codigos (valor) para catalogos y lista `contactos`
    // minimal (idUsuario/nivel). Los campos UI (Opcion) se normalizan aqui.
    const sitio = {
      ...data,
      contactos: arrResponsables.map((arg) => {
        return {
          idUsuario: arg.idUsuario,
          nivel: arg.nivel,
        }
      }),
      razonSocial: data.razonSocial.valor,
      descRazonSocial: data.razonSocial.etiqueta,
      rfc: data.razonSocial.ext5, //ext5 sobre api catalogos es el RFC
      divisionDeNegocio: data.divisionDeNegocio.valor,
      descDivisionDeNegocio: data.divisionDeNegocio.etiqueta,
      procesosActividades: data.procesosActividades?.map((pa: Opcion) => {
        return {
          nombre: pa.valor,
          descripcion: pa.etiqueta,
        }
      }),
      estado: data.estado.valor,
      descEstado: data.estado.etiqueta,
    }
    if (!isModoEditar) {
      console.info('guardar:sitio: ', sitio)
      guardarSitio(sitio)
    } else {
      console.info('actualizar:sitio: ', sitio)
      actualizarSitio(sitio)
    }
  }
  return (
    <>
      <Toaster />
      <div className='container'>
        <div className='row'>
          <div className='mt-2 pb-2 d-flex align-items-center' style={{ marginBottom: '20px' }}>
            {isModoEditar ? (
              isSuccess && (
                <>
                  <h2>Editar sitio</h2>{' '}
                  <div className='subheader-separator subheader-separator-ver mx-5 bg-gray-500'></div>
                  <h4 className='mr-1 fw-light'>{sitio?.nombre}</h4>{' '}
                </>
              )
            ) : (
              <h2>Nuevo sitio</h2>
            )}
          </div>
          <div className='col-12'>
            {isModoEditar && (isLoading || isError) && (
              <CargaInfo
                mensaje={'Recuperando información del sitio'}
                isError={isError}
                mensajeError={'Ocurrió un error al recuperar los datos del sitio'}
                errorApi={error}
              />
            )}
            {(!isModoEditar || (isModoEditar && isSuccess)) && (
              <form onSubmit={handleSubmit(onSubmit)} role='form'>
                <div
                  className={`card ${
                    isLoadingGuardar || isLoadingActualizar ? 'overlay overlay-block' : ''
                  }`}>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-12 mt-1'>
                        <h3>Datos del sitio</h3>
                      </div>
                      <div className='col-12 mb-10' />
                      <div className='col-12'>
                        <div className='row'>
                          <div className='col-5'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='nombre'
                              nombre='Nombre'
                              register={register}
                              errors={errors}
                              maxLength={50}
                              placeholder='Ej. Querétaro'
                              isObligatorio={true}
                            />
                          </div>
                          <div className='col-5'>
                            <Controller
                              control={control}
                              name='razonSocial'
                              render={({ field: { onChange, value } }) => (
                                <FormSelect
                                  className='mb-10'
                                  nombreCampo='razonSocial'
                                  elementos={catRazonesSociales}
                                  nombre='Razón social'
                                  value={value}
                                  placeholder='Selecciona una razón social'
                                  isObligatorio={true}
                                  onChange={(e: Function) => {
                                    onChange(e)
                                  }}
                                  errors={errors}
                                />
                              )}
                            />
                          </div>
                          <div className='col-2' />
                          <div className='col-3'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='nraSemarnat'
                              nombre='NRA SEMARNAT'
                              register={register}
                              errors={errors}
                              maxLength={50}
                              placeholder='Ej. PMA2700500004'
                              isObligatorio={true}
                            />
                          </div>
                          <div className='col-3'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='nraAsea'
                              nombre='NRA ASEA'
                              maxLength={50}
                              register={register}
                              placeholder='Ej. RIM1903700015'
                            />
                          </div>
                          <div className='col-3'>
                            <Controller
                              control={control}
                              name={'divisionDeNegocio'}
                              render={({ field: { onChange, value } }) => (
                                <FormSelect
                                  className='mb-10'
                                  elementos={catDivisionesNegocio}
                                  nombreCampo='divisionDeNegocio'
                                  nombre='División de negocio'
                                  value={value}
                                  placeholder='Ej. Municipal'
                                  isObligatorio={true}
                                  isDisabled={isModoEditar}
                                  onChange={(e: any) => {
                                    onChange(e)
                                    setDivisionNegocio(e ? e.valor : undefined)
                                  }}
                                  errors={errors}
                                />
                              )}
                            />
                          </div>
                          <div className='col-9'>
                            <Controller
                              control={control}
                              name='procesosActividades'
                              render={({ field: { onChange, value } }) => (
                                <FormSelect
                                  className='mb-10'
                                  isMulti={true}
                                  nombreCampo='procesoActividad'
                                  nombre='Proceso o actividad'
                                  value={value}
                                  elementos={catProcesosActividades}
                                  onChange={(e: Function) => {
                                    onChange(e)
                                  }}
                                  placeholder='Ej. Recolección'
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-12 mb-10 border-bottom' />
                      <div className='col-12 mt-1 mb-10'>
                        <h3>Dirección</h3>
                      </div>
                      <div className='col-12'>
                        <div className='row'>
                          <div className='col-5'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='calleNumero'
                              nombre='Calle y número'
                              register={register}
                              errors={errors}
                              placeholder='Ej. Carlos B Zetina'
                              isObligatorio={true}
                              maxLength={300}
                            />
                          </div>
                          <div className='col-2'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='codigoPostal'
                              nombre='Código postal'
                              register={register}
                              errors={errors}
                              placeholder='Ej. 56453'
                              isObligatorio={true}
                              maxLength={5}
                            />
                          </div>
                          <div className='col-4'>
                            <Controller
                              control={control}
                              name='estado'
                              render={({ field: { onChange, value } }) => (
                                <FormSelect
                                  className='mb-10'
                                  elementos={catEstados}
                                  nombre='Estado'
                                  value={value}
                                  nombreCampo='estado'
                                  placeholder='Selecciona un estado'
                                  isObligatorio={true}
                                  onChange={(e: Function) => {
                                    onChange(e)
                                  }}
                                  errors={errors}
                                />
                              )}
                            />
                          </div>
                          <div className='col-1' />
                          <div className='col-4'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='municipio'
                              nombre='Municipio'
                              register={register}
                              errors={errors}
                              placeholder='Ej. Mexicali'
                              isObligatorio={true}
                              maxLength={300}
                            />
                          </div>
                          <div className='col-4'>
                            <FormInput
                              className='mb-10'
                              nombreCampo='colonia'
                              nombre='Colonia'
                              register={register}
                              errors={errors}
                              placeholder='Ej. González Ortega'
                              isObligatorio={true}
                              maxLength={100}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-12 mb-10 border-bottom' />
                      <Contactos
                        isModoEditar={isModoEditar}
                        agregarNivel={agregarNivel}
                        eliminarNivel={eliminarNivel}
                        eliminarResponsable={eliminarResponsable}
                        formSitio={formSitio}
                        arrResponsables={arrResponsables}
                        nivel={nivelGlobal}
                        agregarResponsableCard={agregarResponsableCard}
                        handleDragEnd={handleDragEnd}
                        isBusquedaDeshabilitada={isBusquedaDeshabilitada}
                        agregaOpcionesSelect={agregaOpcionesSelect}
                      />
                    </div>
                    {(isLoadingGuardar || isLoadingActualizar) && (
                      <Cargando mensaje='Guardando Sitio' />
                    )}
                  </div>
                  <div className='card-footer'>
                    <div className='col-12 d-flex justify-content-end'>
                      <Link to='/sitios'>
                        <button className='btn me-3' type='button'>
                          Cancelar
                        </button>
                      </Link>
                      <button className='btn btn-primary p-3' type='submit'>
                        {`${!isModoEditar ? 'Guardar' : 'Actualizar'} sitio`}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AgregarEditarSitio
